import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';

/**
 * Public task contract for the Gantt wrapper.
 * Consumers map their domain objects to this shape.
 */
export interface GanttTask {
  id: number | string;
  parentId?: number | string | null;
  title: string;
  start: Date | string | null;
  end: Date | string | null;
  /** Optional bar color (hex/rgb). Falls back to defaultColor. */
  color?: string;
  /** If true, this row is a parent/summary bar. Currently rendered same as a regular bar. */
  isSummary?: boolean;
  /** Pass-through original record for click events. */
  meta?: any;
}

export type GanttViewType = 'week' | 'month' | 'year' | 'sprintMonth';

/** Internal row model for the left-panel display. */
export interface GanttDisplayRow {
  id: string;
  title: string;
  durationSprints: string;
  startLabel: string;
  endLabel: string;
  depth: number;
  hasChildren: boolean;
  isExpanded: boolean;
  task: GanttTask;
}

let __ganttUid = 0;

/**
 * AscendGanttComponent — amCharts 4 backed Gantt chart.
 *
 * Replaces the previous Kendo UI for jQuery Gantt usage. Renders a horizontal
 * bar timeline using XYChart + DateAxis(X) + CategoryAxis(Y) + ColumnSeries.
 *
 * NOTE: amCharts 4 is in attribution / free-license mode here
 * (`am4core.options.commercialLicense = false`), matching the rest of this
 * codebase. If commercial use without attribution is required, an amCharts
 * license key must be set instead.
 */
@Component({
  selector: 'lib-ascend-gantt',
  templateUrl: './ascend-gantt.component.html',
  styleUrls: ['./ascend-gantt.component.scss'],
  standalone: false,
})
export class AscendGanttComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() tasks: GanttTask[] = [];
  @Input() range: { start: Date | null; end: Date | null } | null = null;
  @Input() viewType: GanttViewType = 'week';
  @Input() height = 270;
  @Input() defaultColor = '#007CB0';
  @Input() rowHeight = 57;
  /** Show task title labels on the Y axis. */
  @Input() showTitleColumn = true;
  /** Disable click emission entirely (e.g. for read-only external users). */
  @Input() readonly = false;
  /** Render a single combined "Sprint Data" column on the left panel instead of the 4-column Phases layout. */
  @Input() singleColumn = false;
  /** Header label for the single-column mode. */
  @Input() singleColumnHeader = 'Sprint Data';
  /** Show edit icon button for sub-phases (child tasks). */
  @Input() showEditIcon = false;
  /** Path to the edit icon image. */
  @Input() editIconSrc = 'assets/psg/Vectoredit.png';

  @Output() taskClick = new EventEmitter<GanttTask>();
  @Output() editClick = new EventEmitter<GanttTask>();

  @ViewChild('chartDiv', { static: true }) chartDiv!: ElementRef<HTMLDivElement>;

  readonly domId = `ascend-gantt-${++__ganttUid}`;

  /** Rows to display in the left panel (phases table). */
  displayRows: GanttDisplayRow[] = [];
  /** Width of the left panel in pixels. */
  leftPanelWidth = 380;

  private chart: am4charts.XYChart | null = null;
  private dateAxis: am4charts.DateAxis | null = null;
  private categoryAxis: am4charts.CategoryAxis | null = null;
  private series: am4charts.ColumnSeries | null = null;
  /** Vertical tick lines drawn in the header band at each grid date. */
  private headerGridLines: am4core.Rectangle[] = [];
  /** Single horizontal line at the bottom of the header band. */
  private headerBottomBorder: am4core.Rectangle | null = null;
  /** IDs of parent tasks whose children are currently expanded. */
  private expanded = new Set<string>();
  /** Hide date labels on days other than Monday (used in week view). */
  private hideNonMondayLabels = false;
  /** Dragging state for the divider. */
  private isDragging = false;
  private dragStartX = 0;
  private dragStartWidth = 0;
  private boundMouseMove: ((e: MouseEvent) => void) | null = null;
  private boundMouseUp: ((e: MouseEvent) => void) | null = null;

  constructor(private zone: NgZone) {}

  ngOnInit(): void {
    // In single-column mode the left panel only needs to fit one column.
    // Apply this before AfterViewInit so the template binding sees the
    // adjusted width during the first change-detection pass.
    if (this.singleColumn && this.leftPanelWidth === 380) {
      this.leftPanelWidth = 240;
    }
    // Populate displayRows synchronously from the current @Input() tasks so
    // the left-panel "Sprint Data" rows paint in the same frame as the chart
    // shell. Without this, rows only appear after ngAfterViewInit -> microtask
    // -> buildChart -> applyData, which looks like the data is "lagging".
    this.populateDisplayRowsEarly();
  }

  ngAfterViewInit(): void {
    // Build outside Angular to keep change detection out of amCharts internals.
    // Defer to a microtask so any synchronous mutation in buildChart/applyData
    // happens after the current change-detection cycle has finished checking,
    // avoiding ExpressionChangedAfterItHasBeenCheckedError.
    this.zone.runOutsideAngular(() => {
      Promise.resolve().then(() => {
        this.buildChart();
        this.applyData();
        this.applyRange();
        this.applyView();
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Always refresh the left-panel rows from `tasks` as soon as the input
    // changes, even before the chart has been built. The chart itself is
    // updated below only after buildChart() has run.
    if (changes['tasks']) {
      this.populateDisplayRowsEarly();
    }
    if (!this.chart) {
      return;
    }
    this.zone.runOutsideAngular(() => {
      if (changes['tasks']) {
        this.applyData();
      }
      if (changes['range']) {
        this.applyRange();
      }
      if (changes['viewType']) {
        this.applyView();
      }
    });
  }

  /* ===== Imperative API (parity with old Kendo wrapper methods) ===== */

  refresh(): void {
    this.zone.runOutsideAngular(() => {
      if (this.chart) {
        this.chart.invalidateData();
      }
    });
  }

  resize(): void {
    // amCharts handles resize automatically via ResizeObserver, but provide a hook.
    this.refresh();
  }

  setRange(start: Date | null, end: Date | null): void {
    this.range = { start, end };
    this.zone.runOutsideAngular(() => this.applyRange());
  }

  setView(view: GanttViewType): void {
    this.viewType = view;
    this.zone.runOutsideAngular(() => this.applyView());
  }

  exportPdf(_fileName?: string): void {
    if (!this.chart) {
      return;
    }
    this.zone.runOutsideAngular(() => {
      // amCharts exporting auto-loads pdfmake on demand.
      this.chart!.exporting.export('pdf');
    });
  }

  ngOnDestroy(): void {
    this.zone.runOutsideAngular(() => {
      if (this.chart) {
        this.chart.dispose();
        this.chart = null;
      }
    });
    // Clean up drag listeners.
    if (this.boundMouseMove) {
      document.removeEventListener('mousemove', this.boundMouseMove);
    }
    if (this.boundMouseUp) {
      document.removeEventListener('mouseup', this.boundMouseUp);
    }
  }

  /* ===== Left panel / Divider ===== */

  /** Called when user clicks the expand/collapse icon in the left panel. */
  onToggleExpand(row: GanttDisplayRow): void {
    const id = row.id;
    if (this.expanded.has(id)) {
      this.expanded.delete(id);
    } else {
      this.expanded.add(id);
    }
    this.zone.runOutsideAngular(() => this.applyData());
  }

  /** Called when user clicks the edit icon for a task row. */
  onEditIconClick(event: Event, row: GanttDisplayRow): void {
    event.stopPropagation();
    if (!this.readonly) {
      this.editClick.emit(row.task);
    }
  }

  /** Start dragging the divider to resize panels. */
  onDividerMouseDown(event: MouseEvent): void {
    event.preventDefault();
    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.dragStartWidth = this.leftPanelWidth;

    this.boundMouseMove = this.onDividerMouseMove.bind(this);
    this.boundMouseUp = this.onDividerMouseUp.bind(this);
    document.addEventListener('mousemove', this.boundMouseMove);
    document.addEventListener('mouseup', this.boundMouseUp);
  }

  private onDividerMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    const delta = event.clientX - this.dragStartX;
    const newWidth = Math.max(280, Math.min(700, this.dragStartWidth + delta));
    this.leftPanelWidth = newWidth;
  }

  private onDividerMouseUp(_event: MouseEvent): void {
    this.isDragging = false;
    if (this.boundMouseMove) {
      document.removeEventListener('mousemove', this.boundMouseMove);
    }
    if (this.boundMouseUp) {
      document.removeEventListener('mouseup', this.boundMouseUp);
    }
    // Trigger chart resize after panel resize.
    this.refresh();
  }

  /* ===== internals ===== */

  private buildChart(): void {
    am4core.options.commercialLicense = false;
    am4core.useTheme(am4themes_animated);

    const chart = am4core.create(this.chartDiv.nativeElement, am4charts.XYChart);
    chart.paddingRight = 20;
    chart.paddingLeft = 0;
    chart.paddingTop = 0;
    chart.paddingBottom = 0;

    // Pin the top date-axis band to a fixed height (matches the CSS height
    // of `.ascend-gantt-header` in the left panel) so the first chart row
    // aligns vertically with the first left-panel row, and the plot area
    // is exactly `rowCount * rowHeight` so each chart row matches the
    // left-panel row height.
    chart.topAxesContainer.minHeight = 60;
    chart.topAxesContainer.maxHeight = 60;
    // IMPORTANT: leave topAxesContainer transparent. amCharts paints the
    // top-axis container's background AFTER the plot's grid lines, so a
    // white fill here would cover the upper portion of the grid lines we
    // extend into the 60px header band below. The host (.ascend-gantt-chart
    // / app) background is already white, so the band still reads as
    // white visually while letting our extended grid lines show through.
    chart.topAxesContainer.background.fillOpacity = 0;
    chart.topAxesContainer.background.strokeOpacity = 0;
    (chart.topAxesContainer as any).mask = undefined;

    // After every render pass, walk the rendered SVG and rewrite the `d`
    // attribute of grid lines that fall on the "major" cadence so they
    // start 60px above the plot top (i.e. extend up into the header band).
    // Finer (intermediate) grid lines keep their original "M0,0 L0,228"
    // path so they remain only within the plot rows.
    //
    //   Phase views (week / month / year): major cadence = weekly  (Mondays)
    //   Sprint view  (sprintMonth):        major cadence = monthly (1st of month)
    //
    // amCharts emits each grid line as:
    //   <g stroke=... transform="translate(X,0)"><path d=" M0,0  L0,228 " ...></g>
    // We use the X translate value plus the dateAxis to recover the date,
    // then decide whether to extend the path upward.
    const patchHeaderGridLines = () => {
      const svgRoot = this.chartDiv?.nativeElement?.querySelector('svg');
      if (!svgRoot || !this.dateAxis) {
        return;
      }
      const isMajorBoundary = (date: Date): boolean => {
        if (this.viewType === 'sprintMonth' || this.viewType === 'year') {
          // Month boundary: first day of the month.
          return date.getDate() === 1;
        }
        // Phase 'week' / 'month' views: week boundary = Monday.
        return date.getDay() === 1;
      };
      // Walk every grid line path. Use the parent <g>'s translate(X,0)
      // to map the X position back to a date via dateAxis.positionToDate.
      const gridPaths = svgRoot.querySelectorAll(
        'g[stroke="#d1d3d6"] > path, g[stroke="#D1D3D6"] > path, g[stroke="#e32c2c"] > path, g[stroke="#E32C2C"] > path',
      );
      gridPaths.forEach((p: Element) => {
        const path = p as SVGPathElement;
        const d = path.getAttribute('d');
        if (!d) return;
        // Only touch vertical grid lines (those starting at the plot top).
        const m = d.match(/^\s*M0,(?:-?\d+(?:\.\d+)?)\s+L0,(\d+(?:\.\d+)?)\s*$/);
        if (!m) return;
        const plotBottom = m[1];
        // Parent group carries transform="translate(X,0)" with the x-pixel.
        const parentG = path.parentElement as unknown as SVGGElement | null;
        const tx = parentG?.getAttribute('transform') || '';
        const tm = tx.match(/translate\(([-\d.]+)\s*,/);
        const xPx = tm ? parseFloat(tm[1]) : 0;
        // Red start-date marker is special-cased: it always extends to the
        // top so the project-start line is continuous in the header too.
        const strokeColor = (parentG?.getAttribute('stroke') || '').toLowerCase();
        const isStartMarker = strokeColor === '#e32c2c';
        let extend = isStartMarker;
        if (!extend) {
          try {
            const pos = this.dateAxis!.pointToPosition({ x: xPx, y: 0 });
            const dt = this.dateAxis!.positionToDate(pos);
            if (dt) {
              extend = isMajorBoundary(dt);
            }
          } catch {
            extend = false;
          }
        }
        const newTop = extend ? -60 : 0;
        path.setAttribute('d', `M0,${newTop} L0,${plotBottom}`);
      });
    };
    chart.events.on('ready', () => requestAnimationFrame(patchHeaderGridLines));
    chart.events.on('datavalidated', () => requestAnimationFrame(patchHeaderGridLines));
    chart.events.on('sizechanged', () => requestAnimationFrame(patchHeaderGridLines));

    // Y axis: tasks (category). Labels are now in the left panel, so hide them here.
    const categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = 'rowKey';
    categoryAxis.renderer.grid.template.location = 1;
    categoryAxis.renderer.grid.template.stroke = am4core.color('#D1D3D6');
    categoryAxis.renderer.grid.template.strokeOpacity = 1;
    categoryAxis.renderer.grid.template.strokeWidth = 1;
    categoryAxis.renderer.line.strokeOpacity = 0;
    categoryAxis.renderer.inversed = true; // first task at the top
    categoryAxis.renderer.minGridDistance = 1;
    categoryAxis.renderer.cellStartLocation = 0;
    categoryAxis.renderer.cellEndLocation = 1;
    // Hide Y-axis labels (shown in left panel table instead).
    categoryAxis.renderer.labels.template.disabled = true;

    // X axis: dates (rendered on TOP of chart)
    const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
    dateAxis.renderer.opposite = true;
    dateAxis.renderer.grid.template.location = 0;
    dateAxis.renderer.grid.template.stroke = am4core.color('#D1D3D6');
    dateAxis.renderer.grid.template.strokeOpacity = 1;
    dateAxis.renderer.grid.template.strokeWidth = 1;
    dateAxis.renderer.grid.template.above = false;

    // Color/widen the grid line that falls on the project start date.
    const isStartDate = (v: number | undefined): boolean => {
      if (!v || !this.range || !this.range.start) return false;
      const d = new Date(v);
      const s = this.range.start;
      return (
        d.getFullYear() === s.getFullYear() &&
        d.getMonth() === s.getMonth() &&
        d.getDate() === s.getDate()
      );
    };
    dateAxis.renderer.grid.template.adapter.add('stroke', (stroke, target) => {
      const di: any = (target as any).dataItem;
      if (isStartDate(di && di.value)) {
        return am4core.color('#E32C2C');
      }
      return stroke;
    });
    dateAxis.renderer.grid.template.adapter.add('strokeWidth', (sw, target) => {
      const di: any = (target as any).dataItem;
      if (isStartDate(di && di.value)) {
        return 2;
      }
      return sw;
    });
    // Horizontal divider at the header/plot seam is now drawn in CSS via
    // .ascend-gantt-chart::before at top:60px. That guarantees pixel-
    // perfect alignment with the left panel's .ascend-gantt-header
    // border-bottom (both anchored to y=60 of the same flex parent).
    // The amCharts axis line is kept hidden because its sub-pixel
    // position (stroke half-offset) does not align reliably with CSS.
    dateAxis.renderer.line.strokeOpacity = 0;
    dateAxis.renderer.labels.template.fontSize = 11;
    dateAxis.renderer.labels.template.fill = am4core.color('#5B6471');
    // Bold date labels in the header band.
    dateAxis.renderer.labels.template.fontWeight = '700';
    dateAxis.renderer.labels.template.paddingTop = 0;
    dateAxis.renderer.labels.template.paddingBottom = 0;
    // Anchor each label at the grid line position (location=0) with the
    // text starting to the RIGHT of the line (horizontalCenter='left'),
    // with a small gap. Vertically center the label within the 60px
    // header band (valign='middle') so it sits midway between the top of
    // the chart and the header/plot seam.
    dateAxis.renderer.labels.template.location = 0;
    dateAxis.renderer.labels.template.horizontalCenter = 'left';
    dateAxis.renderer.labels.template.textAlign = 'start';
    dateAxis.renderer.labels.template.paddingLeft = 6;
    dateAxis.renderer.labels.template.paddingRight = 0;
    dateAxis.renderer.labels.template.verticalCenter = 'middle';
    (dateAxis.renderer.labels.template as any).valign = 'middle';
    dateAxis.renderer.minGridDistance = 70;
    dateAxis.startLocation = 0;
    dateAxis.endLocation = 1;

    // Hide labels for non-Monday days when in week view (daily grid lines,
    // weekly labels). When the project start isn't a Monday, the snapped
    // leading Monday's label is replaced with the actual start date.
    // When the project end isn't a Monday, the trailing Monday's label is
    // replaced with the actual end date.
    dateAxis.renderer.labels.template.adapter.add('text', (text, target) => {
      const di: any = target.dataItem;
      const d: Date | undefined = di && di.date;
      // sprintMonth view: weekly grid lines must be blank; month names are
      // drawn separately as axisRange labels (which carry isRange === true
      // and are passed through untouched).
      if (this.viewType === 'sprintMonth') {
        if (di && di.isRange) {
          return text;
        }
        return '';
      }
      if (d && this.range && this.range.start && this.range.start.getDay() !== 1) {
        const s = this.range.start;
        const lead = new Date(s.getTime());
        const dow = lead.getDay();
        const daysSinceMonday = (dow + 6) % 7;
        lead.setDate(lead.getDate() - daysSinceMonday);
        lead.setHours(0, 0, 0, 0);
        if (
          d.getFullYear() === lead.getFullYear() &&
          d.getMonth() === lead.getMonth() &&
          d.getDate() === lead.getDate()
        ) {
          return AscendGanttComponent.fmtAxisDate(s);
        }
      }
      if (d && this.range && this.range.end) {
        const e = this.range.end;
        const trail = new Date(e.getTime());
        const edow = trail.getDay();
        const daysToNextMonday = edow === 1 ? 0 : (8 - edow) % 7;
        trail.setDate(trail.getDate() + daysToNextMonday);
        trail.setHours(0, 0, 0, 0);
        if (
          d.getFullYear() === trail.getFullYear() &&
          d.getMonth() === trail.getMonth() &&
          d.getDate() === trail.getDate()
        ) {
          // Trailing Monday: end-date label is rendered as a dedicated axis
          // range, so hide the regular Monday label here to avoid duplicates.
          return '';
        }
      }
      if (!this.hideNonMondayLabels) {
        return text;
      }
      if (d && d.getDay() !== 1) {
        return '';
      }
      return text;
    });

    // Series: bars from openDateX -> dateX on each category row
    const series = chart.series.push(new am4charts.ColumnSeries());
    series.dataFields.openDateX = 'startDate';
    series.dataFields.dateX = 'endDate';
    series.dataFields.categoryY = 'rowKey';
    series.clustered = false;

    const col = series.columns.template;
    col.height = am4core.percent(22);
    col.column.cornerRadiusTopLeft = 8;
    col.column.cornerRadiusTopRight = 8;
    col.column.cornerRadiusBottomLeft = 8;
    col.column.cornerRadiusBottomRight = 8;
    col.strokeWidth = 0;
    // Disable tooltip on bar hover.
    col.tooltipText = '';

    // Full-row background series for hover highlight. Drawn behind the main
    // bars; spans the whole visible date range per row.
    const bgSeries = chart.series.push(new am4charts.ColumnSeries());
    bgSeries.dataFields.openDateX = '__bgStart';
    bgSeries.dataFields.dateX = '__bgEnd';
    bgSeries.dataFields.categoryY = 'rowKey';
    bgSeries.clustered = false;
    bgSeries.toBack();
    const bgCol = bgSeries.columns.template;
    bgCol.height = am4core.percent(100);
    bgCol.strokeOpacity = 0;
    // Row-hover background: translucent selection-style highlight (light
    // blue at low opacity) instead of an opaque grey wash, so the bars and
    // grid lines remain visible through it.
    bgCol.fill = am4core.color('#3CABFF');
    bgCol.fillOpacity = 0;
    bgCol.tooltipText = '';
    const bgHover = bgCol.states.create('hover');
    bgHover.properties.fillOpacity = 0.15;

    const defaultColor = this.defaultColor;
    col.adapter.add('fill', (fill, target) => {
      const ctx: any = target.dataItem && (target.dataItem as any).dataContext;
      if (ctx && ctx.color) {
        return am4core.color(ctx.color);
      }
      return am4core.color(defaultColor);
    });
    col.adapter.add('stroke', (stroke, target) => {
      const ctx: any = target.dataItem && (target.dataItem as any).dataContext;
      if (ctx && ctx.color) {
        return am4core.color(ctx.color);
      }
      return am4core.color(defaultColor);
    });

    col.events.on('hit', (ev) => {
      // Click on bars intentionally disabled.
    });
    col.cursorOverStyle = am4core.MouseCursorStyle.default;

    // Horizontal scroll is handled by CSS overflow on the host container.
    // Do NOT add chart.scrollbarX — that creates a drag-to-pan UX.

    // Export menu intentionally disabled (no image/data/print dropdown).

    this.chart = chart;
    this.dateAxis = dateAxis;
    this.categoryAxis = categoryAxis;
    this.series = series;
  }

  /** Recursively compute min start / max end of all descendants of a parent id. */
  private aggregateChildRange(
    parentId: string,
    childrenByParent: Map<string, GanttTask[]>,
  ): { start: Date | null; end: Date | null } {
    let minStart: Date | null = null;
    let maxEnd: Date | null = null;
    const visit = (pid: string) => {
      const kids = childrenByParent.get(pid) || [];
      kids.forEach((c) => {
        if (c.start) {
          const s = new Date(c.start as any);
          if (!minStart || s < minStart) minStart = s;
        }
        if (c.end) {
          const e = new Date(c.end as any);
          if (!maxEnd || e > maxEnd) maxEnd = e;
        }
        visit(String(c.id));
      });
    };
    visit(parentId);
    return { start: minStart, end: maxEnd };
  }

  /** Visible axis span used for the per-row hover background. */
  private computeAxisSpan(): { start: Date; end: Date } | null {
    if (!this.range || !this.range.start || !this.range.end) {
      return null;
    }
    const axisStart = new Date(this.range.start.getTime());
    const dow = axisStart.getDay();
    const daysSinceMonday = (dow + 6) % 7;
    axisStart.setDate(axisStart.getDate() - daysSinceMonday);
    axisStart.setHours(0, 0, 0, 0);
    return { start: axisStart, end: new Date(this.range.end.getTime()) };
  }

  private applyData(): void {
    if (!this.chart) {
      return;
    }
    const rows = this.buildOrderedRows();

    // Stamp full-row background span based on current visible range so the
    // hover highlight covers the entire row width.
    const bg = this.computeAxisSpan();
    if (bg) {
      rows.forEach((r) => {
        r.__bgStart = bg.start;
        r.__bgEnd = bg.end;
      });
    }

    this.chart.data = rows;

    // Populate displayRows for the left panel from the same row data.
    this.displayRows = this.mapDisplayRows(rows);

    // Auto-size container to fit visible rows.
    const rowCount = rows.length || 1;
    const computed = Math.max(this.height, rowCount * this.rowHeight + 60);
    const host = this.chart.svgContainer && this.chart.svgContainer.htmlElement;
    if (host) {
      host.style.height = `${computed}px`;
    }
  }

  /**
   * Build the ordered row data from `this.tasks`. Does NOT touch the chart,
   * so it is safe to call before `buildChart()` runs in ngAfterViewInit.
   * This lets the left-panel "Sprint Data" rows render in the same paint as
   * the chart frame instead of appearing a microtask later.
   */
  private buildOrderedRows() {
    const all = this.tasks || [];

    // Build parent -> children map and identify which tasks have children.
    const childrenByParent = new Map<string, GanttTask[]>();
    const hasChildren = new Set<string>();
    const byId = new Map<string, GanttTask>();
    all.forEach((t) => byId.set(String(t.id), t));
    all.forEach((t) => {
      const pid = t.parentId != null ? String(t.parentId) : '';
      if (pid && byId.has(pid)) {
        hasChildren.add(pid);
        if (!childrenByParent.has(pid)) {
          childrenByParent.set(pid, []);
        }
        childrenByParent.get(pid)!.push(t);
      }
    });

    // Roots: tasks with no parent, or whose parent isn't in the list.
    const roots = all.filter((t) => {
      const pid = t.parentId != null ? String(t.parentId) : '';
      return !pid || !byId.has(pid);
    });

    // Depth-first traversal honoring expanded set.
    const ordered: Array<{ task: GanttTask; depth: number }> = [];
    const walk = (task: GanttTask, depth: number) => {
      ordered.push({ task, depth });
      const idStr = String(task.id);
      if (hasChildren.has(idStr) && this.expanded.has(idStr)) {
        (childrenByParent.get(idStr) || []).forEach((c) => walk(c, depth + 1));
      }
    };
    roots.forEach((r) => walk(r, 0));

    return ordered.map(({ task: t, depth }, i) => {
      const idStr = String(t.id);
      const isParent = hasChildren.has(idStr);

      // For parents, ALWAYS derive start/end from the union of children
      // (ignore the parent's own Start/End values). For leaves, use the task's
      // own dates.
      let start: Date | null;
      let end: Date | null;
      if (isParent) {
        const range = this.aggregateChildRange(idStr, childrenByParent);
        start = range.start;
        end = range.end;
      } else {
        start = t.start ? new Date(t.start as any) : null;
        end = t.end ? new Date(t.end as any) : null;
      }

      const rangeLabel = start && end
        ? `${AscendGanttComponent.fmtDate(start)} - ${AscendGanttComponent.fmtDate(end)}`
        : '';

      return {
        rowKey: `${t.id}__${i}`,
        title: t.title || '',
        color: t.color,
        startDate: start,
        endDate: end,
        startLabel: start ? AscendGanttComponent.fmtDate(start) : '',
        endLabel: end ? AscendGanttComponent.fmtDate(end) : '',
        rangeLabel,
        depth,
        hasChildren: isParent,
        isExpanded: this.expanded.has(idStr),
        __task: t,
        __bgStart: null as Date | null,
        __bgEnd: null as Date | null,
      };
    });
  }

  /** Map ordered rows to the left-panel display model. Pure data, no chart deps. */
  private mapDisplayRows(rows: ReturnType<AscendGanttComponent['buildOrderedRows']>): GanttDisplayRow[] {
    return rows.map((r) => {
      const meta = r.__task.meta || {};
      // Build "Xwd / Ysp" string from duration (days) and sprints.
      let days = 0;
      if (meta.Durationofsprint) {
        days = Number(meta.Durationofsprint) || 0;
      } else if (r.startDate && r.endDate) {
        const dayMs = 24 * 60 * 60 * 1000;
        days = Math.ceil((r.endDate.getTime() - r.startDate.getTime()) / dayMs);
      }
      const sprints = meta.Numberofsprint ? Number(meta.Numberofsprint) : 0;
      const durationSprints = `${days}wd / ${sprints}sp`;
      return {
        id: String(r.__task.id),
        title: r.title,
        durationSprints,
        startLabel: r.startLabel,
        endLabel: r.endLabel,
        depth: r.depth,
        hasChildren: r.hasChildren,
        isExpanded: r.isExpanded,
        task: r.__task,
      };
    });
  }

  /**
   * Populate displayRows from current `this.tasks` without building the chart.
   * Safe to call before ngAfterViewInit; the chart will be built later and
   * applyData() will refresh displayRows again from the same source.
   */
  private populateDisplayRowsEarly(): void {
    this.displayRows = this.mapDisplayRows(this.buildOrderedRows());
  }

  /**
   * Redraw vertical tick lines in the top header band at the x-position of
   * every date-axis grid line and every sprintMonth month axisRange. The
   * lines are short Rectangle children of `chart.topAxesContainer`, which
   * means they appear ONLY in the 60px header strip and are perfectly
   * aligned with the matching vertical grid line in the plot area below.
   */
  private redrawHeaderGridLines(): void {
    if (!this.chart || !this.dateAxis || !this.range) {
      return;
    }
    const range = this.range;
    if (!range.start || !range.end) {
      return;
    }
    // Dispose previously drawn lines.
    this.headerGridLines.forEach(r => r.dispose());
    this.headerGridLines = [];

    const axis = this.dateAxis;
    const topContainer = this.chart.topAxesContainer;
    if (!topContainer) {
      return;
    }

    // If topAxesContainer and plotContainer don't share the same left edge,
    // account for the difference so lines align with the plot grid below.
    const plotLeft = this.chart.plotContainer?.pixelX ?? 0;
    const topLeft = topContainer.pixelX ?? 0;
    const offsetX = plotLeft - topLeft;
    const headerHeight = topContainer.pixelHeight || 60;
    // Slack past the container's bottom edge so each vertical header line
    // bridges any internal seam between topAxesContainer and plotContainer
    // (amCharts4 typically leaves 1-2 px there for the axis line area).
    const bridgeSlack = 4;

    const drawAt = (date: Date) => {
      const point = axis.dateToPoint(date);
      if (!point || !isFinite(point.x)) {
        return;
      }
      const line = topContainer.createChild(am4core.Rectangle);
      line.width = 1;
      line.height = headerHeight + bridgeSlack;
      line.fill = am4core.color('#D1D3D6');
      line.fillOpacity = 1;
      line.strokeOpacity = 0;
      line.isMeasured = false;
      line.x = offsetX + point.x;
      line.y = 0;
      this.headerGridLines.push(line);
    };

    // Compute the typed bounds and step that match what amCharts will render
    // as grid lines for the current view.
    const rawMin = axis.min;
    const rawMax = axis.max;
    const axisMin = typeof rawMin === 'number' ? rawMin : range.start.getTime();
    const axisMax = typeof rawMax === 'number' ? rawMax : range.end.getTime();

    // Walk from the first grid date to the last grid date inclusive, in the
    // step that matches the active view's primary gridInterval.
    const startSnap = new Date(axisMin);
    const advance = (d: Date) => {
      switch (this.viewType) {
        case 'week':
          d.setDate(d.getDate() + 1);
          break;
        case 'year':
          d.setMonth(d.getMonth() + 1);
          d.setDate(1);
          break;
        case 'month':
        case 'sprintMonth':
        default:
          d.setDate(d.getDate() + 7);
          break;
      }
      d.setHours(0, 0, 0, 0);
    };

    // Snap startSnap to the first relevant boundary.
    startSnap.setHours(0, 0, 0, 0);
    if (this.viewType === 'month' || this.viewType === 'sprintMonth') {
      // Snap to Monday on or after axisMin (Monday=1, Sunday=0).
      const dow = startSnap.getDay();
      const daysToMonday = (8 - dow) % 7; // 0 if already Monday
      if (dow !== 1) {
        startSnap.setDate(startSnap.getDate() + daysToMonday);
      }
    } else if (this.viewType === 'year') {
      // Snap to first of the next month if not already on the 1st.
      if (startSnap.getDate() !== 1) {
        startSnap.setMonth(startSnap.getMonth() + 1);
        startSnap.setDate(1);
      }
    }

    const cursor = new Date(startSnap.getTime());
    let safety = 1000;
    while (cursor.getTime() <= axisMax && safety-- > 0) {
      drawAt(new Date(cursor));
      advance(cursor);
    }

    // sprintMonth: also draw a line at every month axisRange (skipping the
    // red sprint-start marker, which draws its own colored grid line).
    if (this.viewType === 'sprintMonth') {
      axis.axisRanges.each(r => {
        const d = (r as any).date as Date | undefined;
        if (!d) return;
        const stroke = (r as any).grid?.stroke;
        const strokeHex = stroke && typeof (stroke as any).hex === 'string'
          ? ((stroke as any).hex as string).toLowerCase()
          : '';
        if (strokeHex === '#e32c2c') return;
        drawAt(d);
      });
    }

    // Draw / reposition the horizontal bottom border of the header on top
    // of all the vertical lines so it's always visible at the seam.
    if (!this.headerBottomBorder) {
      const border = topContainer.createChild(am4core.Rectangle);
      border.fill = am4core.color('#D1D3D6');
      border.fillOpacity = 1;
      border.strokeOpacity = 0;
      border.isMeasured = false;
      this.headerBottomBorder = border;
    } else {
      // Ensure it renders above the vertical lines we just added.
      this.headerBottomBorder.toFront();
    }
    this.headerBottomBorder.width = topContainer.pixelWidth || 0;
    this.headerBottomBorder.height = 1;
    this.headerBottomBorder.x = 0;
    this.headerBottomBorder.y = headerHeight - 1;
  }

  private applyRange(): void {
    if (!this.dateAxis) {
      return;
    }
    // Clear previously-added axis ranges (e.g., start-date marker).
    this.dateAxis.axisRanges.clear();

    if (this.range && this.range.start && this.range.end) {
      const isSprintMonth = this.viewType === 'sprintMonth';

      // Compute the axis bounds. For sprintMonth, snap to the 1st of the
      // month containing range.start and to the 1st of the month AFTER
      // range.end. This guarantees full-month columns so a sprint starting
      // mid-month renders in the correct position within its month (and not
      // pinned to the left edge of the chart).
      const axisStart = new Date(this.range.start.getTime());
      if (isSprintMonth) {
        axisStart.setDate(1);
        axisStart.setHours(0, 0, 0, 0);
      } else {
        const dow = axisStart.getDay(); // 0=Sun..6=Sat
        const daysSinceMonday = (dow + 6) % 7; // Mon=0, Sun=6
        axisStart.setDate(axisStart.getDate() - daysSinceMonday);
        axisStart.setHours(0, 0, 0, 0);
      }

      const axisEnd = new Date(this.range.end.getTime());
      if (isSprintMonth) {
        // First day of the month AFTER range.end so the end month renders
        // in full.
        axisEnd.setDate(1);
        axisEnd.setMonth(axisEnd.getMonth() + 1);
        axisEnd.setHours(0, 0, 0, 0);
      } else {
        const edow = axisEnd.getDay();
        const daysToNextMonday = edow === 1 ? 0 : (8 - edow) % 7;
        axisEnd.setDate(axisEnd.getDate() + daysToNextMonday);
        axisEnd.setHours(0, 0, 0, 0);
      }

      this.dateAxis.min = axisStart.getTime();
      this.dateAxis.max = axisEnd.getTime();
      this.dateAxis.strictMinMax = true;

      // Keep full-row background spans in sync with the visible range.
      if (this.chart && Array.isArray(this.chart.data)) {
        this.chart.data.forEach((r: any) => {
          r.__bgStart = axisStart;
          r.__bgEnd = axisEnd;
        });
        this.chart.invalidateRawData();
      }

      // Highlight every Saturday + Sunday across the visible range with a
      // light-grey background band.
      if (!isSprintMonth) {
        const wkStart = new Date(axisStart.getTime());
        // Move to the first Saturday at or after axisStart.
        const sd = wkStart.getDay(); // 0=Sun..6=Sat
        const daysToSat = (6 - sd + 7) % 7;
        wkStart.setDate(wkStart.getDate() + daysToSat);
        wkStart.setHours(0, 0, 0, 0);
        const endMs = axisEnd.getTime();
        while (wkStart.getTime() <= endMs) {
          const weekendStart = new Date(wkStart.getTime());
          const weekendEnd = new Date(wkStart.getTime());
          weekendEnd.setDate(weekendEnd.getDate() + 2); // Sat + Sun -> Monday 00:00
          const weekendRange = this.dateAxis.axisRanges.create();
          weekendRange.date = weekendStart;
          weekendRange.endDate = weekendEnd;
          weekendRange.axisFill.fill = am4core.color('#EDEDED');
          weekendRange.axisFill.fillOpacity = 1;
          weekendRange.axisFill.above = false;
          weekendRange.grid.strokeOpacity = 0;
          weekendRange.label.disabled = true;
          wkStart.setDate(wkStart.getDate() + 7);
        }
      }

      // Add a red vertical line + label at the project start date so the
      // exact start is shown in the date strip even when grid lines fall on
      // Mondays only.
      // The grid line at the project start date is colored red by the grid
      // template adapter. The leading Monday's axis label is rewritten to show
      // the actual start date (see the labels.template adapter), so no extra
      // axisRange label is needed here.

      // Explicit label at the project end date (the trailing Monday label may
      // be clipped at the axis edge, so add a dedicated axis range label).
      if (!isSprintMonth) {
        const endLabelRange = this.dateAxis.axisRanges.create();
        endLabelRange.date = this.range.end;
        endLabelRange.grid.disabled = true;
        endLabelRange.label.text = AscendGanttComponent.fmtAxisDate(this.range.end);
        endLabelRange.label.fill = am4core.color('#5B6471');
        endLabelRange.label.fontSize = 11;
        endLabelRange.label.fontWeight = '500';
        endLabelRange.label.horizontalCenter = 'left';
        endLabelRange.label.location = 0;
        endLabelRange.label.paddingTop = 4;
        endLabelRange.label.paddingBottom = 4;
        endLabelRange.label.paddingLeft = 4;
      }

      // In sprintMonth mode the grid lines fall on week boundaries (no week
      // date labels), so month names are drawn here as axisRange labels:
      // one per month-start within the visible range, plus an anchor label
      // for the start month at axisStart (since axisStart can be mid-month).
      // A red vertical line is drawn at range.start when it is not the 1st
      // of a month, marking the week in which the first sprint begins.
      if (isSprintMonth) {
        const startsOnFirstOfMonth = this.range.start.getDate() === 1;
        if (!startsOnFirstOfMonth) {
          const startMarker = this.dateAxis.axisRanges.create();
          startMarker.date = this.range.start;
          startMarker.grid.stroke = am4core.color('#E32C2C');
          startMarker.grid.strokeWidth = 2;
          startMarker.grid.strokeOpacity = 1;
          startMarker.grid.above = true;
          startMarker.label.disabled = true;
        }

        // Helper to create a month-name label at a given date.
        const addMonthLabel = (date: Date, monthSource: Date) => {
          const r = this.dateAxis!.axisRanges.create();
          r.date = date;
          r.grid.strokeOpacity = 0;
          r.label.text = `${monthSource.getFullYear()}, ${monthSource.toLocaleString('en-US', { month: 'short' })}`;
          r.label.fill = am4core.color('#5B6471');
          r.label.fontSize = 11;
          r.label.fontWeight = '500';
          r.label.horizontalCenter = 'left';
          r.label.location = 0;
          r.label.paddingTop = 2;
          r.label.paddingBottom = 2;
          r.label.paddingLeft = 4;
        };

        // Iterate every month-start from axisStart up to axisEnd (the 1st of
        // the month AFTER range.end). Each yields a month-name label
        // anchored at the 1st of that month.
        const cursor = new Date(axisStart.getTime());
        while (cursor.getTime() < axisEnd.getTime()) {
          addMonthLabel(new Date(cursor), new Date(cursor));
          cursor.setMonth(cursor.getMonth() + 1);
        }
      }

      // Set a dynamic width so the chart can scroll horizontally.
      const dayMs = 24 * 60 * 60 * 1000;
      const days = Math.ceil((axisEnd.getTime() - axisStart.getTime()) / dayMs);
      const pxPerDay = 25; // adjust as desired
      const minWidth = 800;
      const computedWidth = Math.max(minWidth, days * pxPerDay);
      const host = this.chart?.svgContainer?.htmlElement;
      if (host) {
        host.style.width = `${computedWidth}px`;
      }
    } else {
      this.dateAxis.strictMinMax = false;
    }
  }

  private applyView(): void {
    if (!this.dateAxis) {
      return;
    }
    switch (this.viewType) {
      case 'week':
        // Phase 'week' view: DAILY vertical grid lines in the plot. The
        // SVG post-processor extends only Monday lines up into the header
        // so the header band shows a weekly cadence while plot rows keep
        // the finer daily grid.
        this.dateAxis.renderer.labels.template.disabled = false;
        this.hideNonMondayLabels = true;
        this.dateAxis.baseInterval = { timeUnit: 'day', count: 1 };
        this.dateAxis.gridIntervals.setAll([
          { timeUnit: 'day', count: 1 },
        ]);
        this.dateAxis.dateFormats.setKey('day', 'MMM, d');
        this.dateAxis.periodChangeDateFormats.setKey('day', 'MMM, d');
        this.dateAxis.renderer.minGridDistance = 15;
        break;
      case 'month':
        // Phase 'month' view: DAILY vertical grid lines in the plot;
        // only Monday lines extend into the header (weekly cadence).
        this.dateAxis.renderer.labels.template.disabled = false;
        this.hideNonMondayLabels = false;
        this.dateAxis.baseInterval = { timeUnit: 'day', count: 1 };
        this.dateAxis.gridIntervals.setAll([
          { timeUnit: 'day', count: 1 },
          { timeUnit: 'week', count: 1 },
        ]);
        this.dateAxis.dateFormats.setKey('day', 'MMM, d');
        this.dateAxis.dateFormats.setKey('week', 'MMM, d');
        this.dateAxis.dateFormats.setKey('month', 'MMM, yyyy');
        this.dateAxis.renderer.minGridDistance = 25;
        break;
      case 'year':
        // Phase 'year' view: WEEKLY vertical grid lines in the plot;
        // only month-start lines extend into the header (monthly cadence).
        this.dateAxis.renderer.labels.template.disabled = false;
        this.hideNonMondayLabels = false;
        this.dateAxis.baseInterval = { timeUnit: 'day', count: 1 };
        this.dateAxis.gridIntervals.setAll([
          { timeUnit: 'week', count: 1 },
          { timeUnit: 'month', count: 1 },
        ]);
        this.dateAxis.dateFormats.setKey('week', 'MMM, d');
        this.dateAxis.dateFormats.setKey('month', 'MMM');
        this.dateAxis.dateFormats.setKey('year', 'yyyy');
        this.dateAxis.renderer.minGridDistance = 25;
        break;
      case 'sprintMonth':
        // Sprint-plan view: WEEKLY vertical grid lines in the plot;
        // only month-start lines extend into the header (monthly cadence).
        // No date text on the gridline itself — month names are drawn as
        // axis ranges in applyRange().
        this.dateAxis.renderer.labels.template.disabled = false;
        this.hideNonMondayLabels = false;
        this.dateAxis.baseInterval = { timeUnit: 'day', count: 1 };
        this.dateAxis.gridIntervals.setAll([
          { timeUnit: 'week', count: 1 },
        ]);
        this.dateAxis.dateFormats.setKey('day', '');
        this.dateAxis.dateFormats.setKey('week', '');
        this.dateAxis.dateFormats.setKey('month', '');
        this.dateAxis.periodChangeDateFormats.setKey('day', '');
        this.dateAxis.periodChangeDateFormats.setKey('week', '');
        this.dateAxis.periodChangeDateFormats.setKey('month', '');
        this.dateAxis.renderer.minGridDistance = 25;
        break;
    }
  }

  private static fmtDate(d: Date): string {
    // 'MMM dd, yy'
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: '2-digit',
    });
  }

  /** Compact 'MMM, d' format used in the chart's date axis section. */
  private static fmtAxisDate(d: Date): string {
    const month = d.toLocaleDateString('en-US', { month: 'short' });
    return `${month}, ${d.getDate()}`;
  }
}
